// The presence heartbeat: while the app is in the foreground and presence is on, it reports itself
// on becoming visible and every 15 minutes (docs/design/social-plan.md, "Presence"). Nothing is sent
// while the app is hidden, signed out or switched off, and a tab that flaps in and out of view is
// held to one report a minute, so a heartbeat stays one cheap write a quarter hour.
import type { PresenceActivityReport } from "@proef/contract";
import { PRESENCE_HEARTBEAT_MS, PRESENCE_MIN_GAP_MS } from "@proef/domain";
import { useSyncExternalStore } from "react";
import { getRun, subscribeRun } from "../tasting/store";
import { getAuthState, onSessionChange } from "./auth";
import { sendHeartbeat } from "./presenceApi";
import { activityKey, activityOfRun } from "./presenceActivity";
import { resetPresenceList } from "./presenceList";

/** When this page first saw each run, for a run an older build stored without a start. */
const firstSeen = new Map<string, string>();

/** The person's switch as the edge last said it; undefined until known and after sign-out. */
let enabled: boolean | undefined;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => {
    listener();
  });
}

export function getPresenceEnabled(): boolean | undefined {
  return enabled;
}

/** Records the switch (after the settings call, or the heartbeat's answer); the beacon follows it. */
export function setPresenceEnabledLocal(next: boolean | undefined): void {
  if (next === enabled) return;
  enabled = next;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePresenceEnabled(): boolean | undefined {
  return useSyncExternalStore(subscribe, getPresenceEnabled, () => undefined);
}

/** Tests start from an unknown switch. */
export function resetPresenceBeacon(): void {
  enabled = undefined;
}

function signedInUser(): string | undefined {
  const auth = getAuthState();
  return auth.status === "signedIn" ? auth.user.id : undefined;
}

/** Starts the beacon; returns the stop. App runs it for as long as it is mounted. */
export function startPresenceBeacon(): () => void {
  let timer: ReturnType<typeof setInterval> | undefined;
  let lastBeat = Number.NEGATIVE_INFINITY;
  let lastUser: string | undefined;

  const reporting = (): boolean =>
    signedInUser() !== undefined && enabled !== false && document.visibilityState === "visible";

  /** What this device last got the edge to hold about its tasting ("" for nothing). Only an answer
   * moves it, so an ending that did not arrive is carried by the next heartbeat. */
  let reported = "";

  /** What the last heartbeat sent, answered or not, so a burst of run updates sends one report. */
  let sent = "";

  const currentActivity = (): PresenceActivityReport | null => {
    const run = getRun();
    if (run === undefined) return null;
    const seen = firstSeen.get(run.id) ?? new Date().toISOString();
    firstSeen.set(run.id, seen);
    return activityOfRun(run, Date.now(), seen);
  };

  const beat = (): void => {
    if (!reporting()) return;
    const activity = currentActivity();
    const key = activityKey(activity);
    // A tasting starting or ending is worth reporting now; the edge's own rate limit still applies.
    if (key === reported && Date.now() - lastBeat < PRESENCE_MIN_GAP_MS) return;
    lastBeat = Date.now();
    sent = key;
    sendHeartbeat(activity ?? (reported === "" ? undefined : null))
      .then((self) => {
        // The edge decides: a switch turned off on another device stops this one too.
        if (signedInUser() !== undefined) {
          setPresenceEnabledLocal(self.enabled);
          reported = key;
        }
        return undefined;
      })
      .catch(() => {
        sent = reported;
      });
  };

  /** Brings the rhythm in line with what may be reported right now. */
  const sync = (): void => {
    if (timer !== undefined) clearInterval(timer);
    timer = undefined;
    if (!reporting()) return;
    timer = setInterval(beat, PRESENCE_HEARTBEAT_MS);
    beat();
  };

  const onSession = (): void => {
    const user = signedInUser();
    if (user !== lastUser) {
      lastUser = user;
      lastBeat = Number.NEGATIVE_INFINITY;
      reported = "";
      sent = "";
      // Whoever signs in next gets their own switch from their first heartbeat.
      setPresenceEnabledLocal(undefined);
      resetPresenceList();
    }
    sync();
  };

  let wasEnabled = enabled;
  const onEnabled = (): void => {
    // Back on means the person wants to show up now, minute gap or not.
    if (enabled === true && wasEnabled === false) lastBeat = Number.NEGATIVE_INFINITY;
    wasEnabled = enabled;
    sync();
  };

  const stopSession = onSessionChange(onSession);
  const stopEnabled = subscribe(onEnabled);
  // A tasting that begins or ends reports itself at once instead of waiting out the quarter hour.
  const stopRun = subscribeRun(() => {
    if (reporting() && activityKey(currentActivity()) !== sent) beat();
  });
  document.addEventListener("visibilitychange", sync);
  lastUser = signedInUser();
  sync();
  return () => {
    stopSession();
    stopEnabled();
    stopRun();
    document.removeEventListener("visibilitychange", sync);
    if (timer !== undefined) clearInterval(timer);
  };
}
